import { useQuery, useMutation } from "react-query";
import { useAppContext } from "../../AppProviders";
import { API_URL, HOOKS_API_URL } from "../helpers";
import { LegacyUser, LegacyUserWithCompanyName } from "./LegacyUser";
import { useGetCompanyBase } from "../CompaniesApi";
import axios from "axios";

export function useFetchAllUsers() {
  const { requestApi } = useAppContext();
  const { mutateAsync: getCompanyBase } = useGetCompanyBase();

  return useQuery(
    "legacyUsers",
    async (): Promise<LegacyUserWithCompanyName[]> => {
      const users: LegacyUser[] = await requestApi
        .get(`${API_URL}/legacy/users/all`)
        .then((res) => res.data);

      // helpers to fetch company names for user friendly views
      const companyIDs = users.map((u) => u.ossa_company_uuid);
      const uniqueCompanyIds = Array.from(new Set(companyIDs));
      const companies = await Promise.all(
        uniqueCompanyIds.map((companyId) => getCompanyBase(companyId))
      );
      const companyIdToName = Object.fromEntries(
        companies.map((c) => [c.external_id, c.name])
      );
      return users.map((u) => ({
        ossa_company_uuid: u.ossa_company_uuid,
        username: u.username,
        email: u.email,
        company_name: companyIdToName[u.ossa_company_uuid] ?? "Unknown",
      }));
    },
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
    }
  );
}

export function useResetPassword() {
  // we use axios directly here because this hook is
  // meant to be used in an anonymous app context
  const resetPassword = async ({
    token,
    password,
  }: {
    token: string;
    password: string;
  }): Promise<{ success: boolean; error?: string }> => {
    return axios
      .post(`${HOOKS_API_URL}/webhooks?callback_type=LEGACY_PASSWORD_RESET`, {
        token,
        password,
      })
      .then((response) => {
        return response.data;
      });
  };
  return useMutation(resetPassword);
}

export function useRequestPasswordReset() {
  const { requestApi } = useAppContext();
  const requestPasswordReset = async (username: string) =>
    requestApi
      .post(
        `${API_URL}/legacy/users/password-reset-request?username=${encodeURIComponent(
          username
        )}`
      )
      .then((response) => {
        return response.data;
      });
  return useMutation(requestPasswordReset);
}

export function useCreateLegacyUser() {
  const { requestApi } = useAppContext();
  const createLegacyUser = async ({
    ossa_company_uuid,
    username,
    email,
  }: {
    ossa_company_uuid: string;
    username: string;
    email: string;
  }) => {
    return requestApi
      .post(`${API_URL}/legacy/users`, { ossa_company_uuid, username, email })
      .then((res) => res.data);
  };
  return useMutation(createLegacyUser);
}
